import { isDev } from "../../lib";
import { spacing } from "../../tokens";
import type { BottomSheetSnapPoint, BottomSheetSnapPoints } from "./types";

/**
 * The page strip (px) left visible above the "max" surface, below the top
 * safe-area inset, on both platforms: the surface is the screen minus the
 * inset minus this peek.
 */
export const SHEET_PEEK = spacing["3xl"];

const CONTENT: BottomSheetSnapPoints = ["content"];

const isFraction = (point: unknown): point is number =>
  typeof point === "number" && point > 0 && point <= 1;

/**
 * The snap points a sheet actually uses, the same on both platforms.
 *
 * `["content"]` (or nothing) sizes the sheet to its content. Otherwise the
 * points are fractions of the sheet surface in (0, 1], strictly ascending
 * (bottom to top). Anything else, such as `"content"` mixed with fractions or
 * fractions out of order, is invalid: it logs an error in development builds and
 * falls back to a content-sized sheet, one detent at index 0, rather than each
 * platform misreading it differently.
 */
export function resolveSnapPoints(
  snapPoints: readonly BottomSheetSnapPoint[] | undefined,
): BottomSheetSnapPoints {
  if (!snapPoints || (snapPoints.length === 1 && snapPoints[0] === "content")) return CONTENT;

  const ascending =
    snapPoints.length > 0 &&
    snapPoints.every(
      (point, i) => isFraction(point) && (i === 0 || point > (snapPoints[i - 1] as number)),
    );
  if (ascending) return snapPoints as number[];

  if (isDev()) {
    console.error(
      `BottomSheet: invalid snap points ${JSON.stringify(snapPoints)}. Use ["content"] or ` +
        "strictly ascending fractions in (0, 1]; falling back to a content-sized sheet.",
    );
  }
  return CONTENT;
}

export const isContentSized = (points: BottomSheetSnapPoints): points is ["content"] =>
  points[0] === "content";
